import { useCallback, useEffect, useRef, useState } from "react";

export function useContentSearch() {
  const [searchQuery, setSearchQuery]   = useState("");
  const [matchCount, setMatchCount]     = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const contentRef                      = useRef<HTMLElement | null>(null);

  const clearHighlights = (el: HTMLElement) => {
    el.querySelectorAll("mark[data-search]").forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(mark.textContent || ""), mark);
      parent.normalize();
    });
  };

  const scrollToMatch = (index: number, el: HTMLElement) => {
    const marks = el.querySelectorAll("mark[data-search]");
    marks.forEach((m, i) => {
      (m as HTMLElement).style.backgroundColor = i === index ? "#f97316" : "#facc15";
      (m as HTMLElement).style.outline         = i === index ? "2px solid #f97316" : "none";
    });
    (marks[index] as HTMLElement | undefined)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const applyHighlights = useCallback((query: string) => {
    const el = contentRef.current;
    if (!el) return;
    clearHighlights(el);
    setMatchCount(0);
    setCurrentMatch(0);
    if (!query.trim()) return;

    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex   = new RegExp(escaped, "gi");

    const walker    = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) textNodes.push(node);

    let total = 0;
    textNodes.forEach((textNode) => {
      const text = textNode.textContent || "";
      if (!regex.test(text)) return;
      regex.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let last = 0;
      let m: RegExpExecArray | null;
      while ((m = regex.exec(text)) !== null) {
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        const mark = document.createElement("mark");
        mark.setAttribute("data-search", String(total));
        mark.style.backgroundColor = "#facc15";
        mark.style.color           = "#1e293b";
        mark.style.borderRadius    = "2px";
        mark.style.padding         = "0 1px";
        mark.textContent = m[0];
        frag.appendChild(mark);
        total++;
        last = m.index + m[0].length;
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      textNode.parentNode?.replaceChild(frag, textNode);
    });

    setMatchCount(total);
    if (total > 0) { setCurrentMatch(1); scrollToMatch(0, el); }
  }, []);

  const goNext = useCallback(() => {
    const el = contentRef.current;
    if (!el || matchCount === 0) return;
    const next = currentMatch % matchCount;
    setCurrentMatch(next + 1);
    scrollToMatch(next, el);
  }, [matchCount, currentMatch]);

  const goPrev = useCallback(() => {
    const el = contentRef.current;
    if (!el || matchCount === 0) return;
    const prev = (currentMatch - 2 + matchCount) % matchCount;
    setCurrentMatch(prev + 1);
    scrollToMatch(prev, el);
  }, [matchCount, currentMatch]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setMatchCount(0);
    setCurrentMatch(0);
    if (contentRef.current) clearHighlights(contentRef.current);
  }, []);

  // Debounced re-highlight on query change
  useEffect(() => {
    const t = setTimeout(() => applyHighlights(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery, applyHighlights]);

  return {
    searchQuery, setSearchQuery,
    matchCount, currentMatch,
    contentRef,
    goNext, goPrev, clearSearch,
  };
}
